import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { captureException, withScope } from '@sentry/browser';

import type { Dispatch } from 'src/config/rootStore';
import { useBypassAssessmentsMutation } from 'src/modules/onboarding/documents/bypassAssessmentsMutation.generated';
import {
    getMostRecentCareRecommendation,
    getUserInformation,
} from 'src/modules/user/selectors/userSelectors';

import { useStartCareRecommendationMutation } from 'src/modules/onboarding/documents/startCareRecommendationMutation.generated';
import { useHistory } from 'react-router-dom';

import {
    isOnboardedInfoFetched,
    isOnboardingFirstStepCompleted,
    isOnboardingSecondStepCompleted,
} from 'src/modules/onboarding/selectors/onboardingSelectors';
import { useLastVisitedRoute } from 'src/routing/RouteContext';
import { isSSOFlowRegistrationSource } from 'src/shared/helpers/isSSOFlowRegistrationSource';

// accessing any of those pages with onboarding not finished will trigger
// onboarding bypass logic which will allow user to see requested content first
// and then to continue the onboarding from the for you action
const urlsThatSkipsOnboarding = [
    '/pause/',
    '/resources',
    '/meditations',
    '/programs',
    '/program/',
    '/meditation/',
    '/course/',
    '/circles',
    '/private-circles',
];

function useCheckIfUserShouldSkipOnboarding() {
    const [shouldSkipOnboardingChecked, setShouldSkipOnboardingChecked] = useState(false);
    const lastVisitedRoute = useLastVisitedRoute();
    const dispatch = useDispatch<Dispatch>();
    const careRecommendation = useSelector(getMostRecentCareRecommendation);
    const careRecommendationUuidRef = useRef<string | undefined | null>(careRecommendation?.uuid);
    careRecommendationUuidRef.current = careRecommendation?.uuid;
    const [bypassAssessments] = useBypassAssessmentsMutation();
    const [startCareRecommendation] = useStartCareRecommendationMutation();

    const redirectRoute = useMemo(() => {
        if (
            !!lastVisitedRoute?.pathname &&
            urlsThatSkipsOnboarding.some(url => lastVisitedRoute.pathname.startsWith(url))
        ) {
            return lastVisitedRoute;
        }

        return undefined;
    }, [lastVisitedRoute]);

    const checkIfUserShouldSkipOnboarding = useCallback(async () => {
        const isSkipOnboardingPathname = !!redirectRoute;

        if (!isSkipOnboardingPathname) {
            setShouldSkipOnboardingChecked(true);
            return;
        }

        try {
            let careRecUuid = careRecommendationUuidRef.current;

            // there will be no care recommendation if user didn't complete
            // first part of the onboarding yet (topics/categories) so we need
            // to start a new one
            if (!careRecUuid) {
                try {
                    const response = await startCareRecommendation();
                    const uuid = response?.data?.startCareRecommendation?.careRecommendation?.uuid;
                    if (!uuid) {
                        throw new Error(
                            '[Bypass onboarding] Could not start new care recommendation',
                        );
                    }

                    careRecUuid = uuid;

                    await dispatch.user.fetchUserInfo();
                } catch (error) {
                    captureException(error);
                }
            }

            // this will set care recommendation's state to
            // `BypassedAssessments` which informs us that user has skipped the
            // onboarding
            if (careRecUuid) {
                let extraData: unknown = null;
                try {
                    const response = await bypassAssessments({
                        variables: { careRecommendationUuid: careRecUuid },
                    });

                    extraData = response.data;

                    const newCareRecUuid =
                        response?.data?.bypassAssessments?.careRecommendation?.uuid;

                    if (!newCareRecUuid) {
                        throw new Error(
                            '[Bypass onboarding] Did not receive correct care recommendation in the response',
                        );
                    }

                    await dispatch.user.fetchUserInfo();
                } catch (error) {
                    withScope(scope => {
                        scope.setExtra('error_info', 'Error: failed to bypass onboarding');
                        scope.setExtra('response', extraData);
                        captureException(error);
                    });
                }
            }
        } finally {
            setShouldSkipOnboardingChecked(true);
        }
    }, [dispatch.user, startCareRecommendation, bypassAssessments, redirectRoute]);

    return [checkIfUserShouldSkipOnboarding, shouldSkipOnboardingChecked, redirectRoute] as const;
}

export const OnboardingRedirect = () => {
    const history = useHistory();
    const dispatch = useDispatch<Dispatch>();
    const flags = useFlags();
    const user = useSelector(getUserInformation);
    const onboardingInfoFetched = useSelector(isOnboardedInfoFetched);
    const onboardingFirstStepCompleted = useSelector(isOnboardingFirstStepCompleted);
    const onboardingSecondStepCompleted = useSelector(isOnboardingSecondStepCompleted);

    const [checkIfUserShouldSkipOnboarding, shouldSkipOnboardingChecked, skipOnboardingRedirect] =
        useCheckIfUserShouldSkipOnboarding();

    const bypassOnboardingEnabled = !onboardingFirstStepCompleted && !onboardingSecondStepCompleted;

    useEffect(() => {
        // bypassing the onboarding and letting user see the content
        if (bypassOnboardingEnabled && !shouldSkipOnboardingChecked) {
            checkIfUserShouldSkipOnboarding();

            return;
        }
        if (!onboardingInfoFetched) return;

        if (!onboardingFirstStepCompleted) {
            const nextPage = (() => {
                if (user.emailConfirmed) {
                    return '/completion-page';
                }

                if (user.newEmailAddressNotConfirmed) {
                    return '/preferred-email-sent';
                }

                if (isSSOFlowRegistrationSource(user.registrationSource)) {
                    return '/registration';
                }

                return '/preferred-email';
            })();

            history.replace(nextPage);
            return;
        }

        if (!onboardingSecondStepCompleted) {
            history.replace(
                flags?.hideTriageSymptoms ? '/a-few-questions' : '/onboarding-wellness',
            );

            return;
        }

        history.replace(skipOnboardingRedirect ?? '/dashboard');
    }, [
        bypassOnboardingEnabled,
        dispatch.assessments,
        flags?.hideTriageSymptoms,
        history,
        onboardingFirstStepCompleted,
        onboardingInfoFetched,
        onboardingSecondStepCompleted,
        shouldSkipOnboardingChecked,
        skipOnboardingRedirect,
        user.emailConfirmed,
        user.newEmailAddressNotConfirmed,
        user.registrationSource,
        checkIfUserShouldSkipOnboarding,
    ]);

    return <CenteredSpinner />;
};
